// src/components/navigations.jsx - WITH BEAUTIFUL ICONS (HOVER-ENABLED)
import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  // Solid icons
  faChartPie,
  faHome,
  faChartLine,
  faUserTie,
  faChevronDown,
  faChevronRight,
  faCogs,
  faBrain,
  faDatabase,
  faShieldAlt,
  faLeaf,
  faGlobeAmericas,
  faClipboardCheck,
  faSearch,
  faRecycle,
  faLightbulb,
  faCertificate,
  faGraduationCap,
  faBook,
  faUsers,
  faMagnifyingGlassChart,
  faVideo,
  faNewspaper,
  faBriefcase,
  faWrench,
  faChalkboardTeacher,
  faSchool,
  faBookOpen,
  faLaptopCode,
  faExternalLinkAlt,
  faSearchPlus,
  faLink,
  faRss,
  // Regular icons (if needed)
  faFileAlt,
} from "@fortawesome/free-solid-svg-icons";

import { FaYoutube, FaLinkedinIn } from "react-icons/fa";

// Color constants for easy maintenance
const COLORS = {
  primary: "#F56620", // Orange
  primaryHover: "#E05510",
  text: "#2D3748",
  textHover: "#FFFFFF",
  background: "#FFFFFF",
  border: "#EAEAEA",
  dropdownBg: "#FFFFFF",
  dropdownBorder: "#E2E8F0",
  icon: "#4A5568",
  iconHover: "#FFFFFF",
  logo: "#1A365D",
};

// Styled Components
const NavContainer = styled.nav`
  background: ${COLORS.background};
  padding: 0 2rem;
  border-bottom: 1px solid ${COLORS.border};
  width: 100%;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);

  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

const NavInner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 auto;
  height: 70px;
  position: relative;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;

  .logo-img {
    height: 3.5rem;
    width: 3.5rem;

    border-radius: 12px;
    padding: 1px;
    object-fit: cover;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.18);
    transition: all 0.25s ease;
  }

  &:hover .logo-img {
    transform: translateY(-1px) scale(1.05);
  }

  @media (max-width: 768px) {
    .logo-img {
      height: 36px;
      width: 36px;
      border-radius: 10px;
      padding: 3px;
    }
  }
`;

const NavItems = styled.div`
  display: flex;
  gap: 0.2rem;
  align-items: center;
  flex: 1;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavItem = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
`;

const NavLink = styled.a`
  color: ${({ $themeColor }) => $themeColor} !important;
  text-decoration: none !important;

  &:visited,
  &:active,
  &:focus,
  &:link {
    color: ${({ $themeColor }) => $themeColor} !important;
    text-decoration: none !important;
  }

  font-weight: 600;
  padding: 0.75rem 1.25rem;
  font-size: 0.8rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  white-space: nowrap;
  border-radius: 8px;
  position: relative;

  .icon {
    color: ${COLORS.icon};
    font-size: 1.2rem;
    transition: all 0.3s;
  }

  &:hover {
    color: ${COLORS.textHover} !important;
    background-color: ${({ $themeColor }) => $themeColor};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${({ $themeColor }) => hexToRgba($themeColor, 0.3)};

    .icon {
      color: ${COLORS.iconHover} !important;
      transform: scale(1.1);
    }
  }
`;

const NavButton = styled.button`
  color: ${COLORS.text};
  text-decoration: none;
  font-weight: 600;
  padding: 0.75rem 1.25rem;
  font-size: 0.8rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: none;
  border: none;
  cursor: pointer;
  white-space: nowrap;
  border-radius: 8px;
  position: relative;

  .icon {
    color: ${COLORS.icon};
    font-size: 1.2rem;
    transition: all 0.3s;
  }

  &:hover {
    color: ${COLORS.textHover} !important;
    background-color: ${({ $themeColor }) => $themeColor};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${({ $themeColor }) => hexToRgba($themeColor, 0.3)};

    .icon {
      color: ${COLORS.iconHover} !important;
      transform: scale(1.1);
    }

    .dropdown-arrow .icon {
      color: ${COLORS.iconHover} !important;
    }
  }
`;

const DropdownArrow = styled.span`
  display: flex;
  align-items: center;
  margin-left: 4px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  ${(props) =>
    props.$isOpen &&
    `
    transform: rotate(180deg);
  `}

  .icon {
    font-size: 0.9rem;
    color: ${COLORS.icon};
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: ${COLORS.dropdownBg};
  border-radius: 12px;
  padding: 0.75rem 0;
  box-shadow:
    0 20px 40px rgba(0, 0, 0, 0.12),
    0 4px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid ${COLORS.dropdownBorder};
  min-width: 280px;
  z-index: 1000;
  margin-top: 10px;
  animation: fadeIn 0.25s ease-out;
  display: ${(props) => (props.$isOpen ? "block" : "none")};
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.98);

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(-15px);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }
`;

const SubMenu = styled(DropdownMenu)`
  position: absolute;
  left: 100%;
  top: -0.75rem;
  margin-left: 5px;
  min-width: 260px;
  display: ${(props) => (props.$isOpen ? "block" : "none")};
  border-radius: 10px;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  pointer-events: ${(props) => (props.$isOpen ? "auto" : "none")};
  transition: opacity 0.15s ease;

  /* Important: Add an invisible hover area between parent and submenu */
  &::before {
    content: "";
    position: absolute;
    right: 100%;
    top: 0;
    height: 100%;
    width: 15px; /* Increased from 5px to 15px for better hover area */
    background: transparent;
  }
`;

const DropdownList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const DropdownItem = styled.li`
  position: relative;
  padding: 0;
  margin: 0 0.5rem;

  &:first-child {
    margin-top: 0.25rem;
  }

  &:last-child {
    margin-bottom: 0.25rem;
  }

  /* Enable submenu hover with gap */
  &:hover > ${SubMenu} {
    display: block;
    opacity: 1;
    pointer-events: auto;
  }

  /* Keep submenu open when hovering over it */
  & ${SubMenu}:hover {
    display: block;
    opacity: 1;
  }
`;

const DropdownLink = styled.a`
  &&& {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.85rem 1.5rem;

    color: ${({ $themeColor }) => $themeColor} !important;
    text-decoration: none !important;

    &:visited,
    &:active,
    &:focus,
    &:link {
      color: ${({ $themeColor }) => $themeColor} !important;
      text-decoration: none !important;
    }

    font-size: 0.95rem;
    font-weight: 500;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    width: 100%;
    text-align: left;
    cursor: pointer;
    background: none;
    border: none;
    font-family: inherit;
    white-space: nowrap;
    border-radius: 8px;
    position: relative;
    overflow: hidden;

    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 0;
      height: 100%;
      width: 3px;
      background: ${({ $themeColor }) => $themeColor};
      transform: scaleY(0);
      transition: transform 0.3s;
    }

    &:hover {
      color: ${COLORS.textHover} !important;
      background: linear-gradient(
        135deg,
        ${({ $themeColor }) => $themeColor},
        ${({ $themeColor }) => $themeColor}
      );
      transform: translateX(5px);
      box-shadow: 0 4px 12px rgba(1, 115, 186, 0.25);

      &::before {
        transform: scaleY(1);
      }

      .icon {
        color: ${COLORS.iconHover} !important;
        transform: scale(1.15);
      }
    }

    .icon {
      color: ${COLORS.icon} !important;
      font-size: 1.1rem;
      flex-shrink: 0;
      transition: all 0.25s;
      min-width: 20px;
    }
  }
`;

const SubMenuTrigger = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.85rem 1.5rem;
  color: ${COLORS.text};
  text-decoration: none;
  font-size: 0.95rem;
  font-weight: 500;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  width: 100%;
  text-align: left;
  cursor: pointer;
  background: none;
  border: none;
  font-family: inherit;
  white-space: nowrap;
  border-radius: 8px;
  position: relative;
  overflow: visible; /* Changed from 'hidden' to 'visible' */

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 3px;
    background: ${({ $themeColor }) => $themeColor};
    transform: scaleY(0);
    transition: transform 0.3s;
  }

  &:hover {
    color: ${COLORS.textHover} !important;
    background: linear-gradient(
      135deg,
      ${({ $themeColor }) => $themeColor},
      ${({ $themeColor }) => $themeColor}
    );
    transform: translateX(5px);
    box-shadow: 0 4px 12px rgba(245, 102, 32, 0.25);

    &::before {
      transform: scaleY(1);
    }

    .menu-icon .icon {
      color: ${COLORS.iconHover} !important;
      transform: scale(1.15);
    }

    .submenu-arrow .icon {
      color: ${COLORS.iconHover} !important;
      transform: translateX(3px);
    }
  }
`;

const MenuIcon = styled.span`
  display: flex;
  align-items: center;
  gap: 1rem;

  .icon {
    color: ${COLORS.icon};
    font-size: 1.1rem;
    flex-shrink: 0;
    transition: all 0.25s;
    min-width: 20px;
  }
`;

const SubMenuArrow = styled.span`
  display: flex;
  align-items: center;
  transition: all 0.25s;

  .icon {
    font-size: 1rem;
    color: ${COLORS.icon};
  }
`;

const Tagline = styled.div`
  text-align: center;
  padding: 0.6rem 1.5rem;
  border: 2px solid ${({ $themeColor }) => $themeColor};
  border-radius: 50px;
  // background: linear-gradient(
  //   135deg,
  //   rgba(245, 102, 32, 0.1),
  //   rgba(245, 102, 32, 0.05)
  // );
  background: ${({ $themeColor }) => hexToRgba($themeColor, 0.05)};
  box-shadow: 0 4px 15px ${({ $themeColor }) => hexToRgba($themeColor, 0.15)};
  margin-left: 2rem;
  transition: all 0.3s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px ${({ $themeColor }) => hexToRgba($themeColor, 0.25)};
  }

  p {
    color: ${({ $themeColor }) => $themeColor};
    background: ${({ $themeColor }) => $themeColor};
    font-size: 0.9rem;
    margin: 0;
    font-weight: 700;
    letter-spacing: 0.5px;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const ActiveIndicator = styled.div`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 3px;
  background: ${COLORS.primary};
  border-radius: 3px 3px 0 0;
  transition: width 0.3s ease;

  ${NavButton}:hover & {
    width: 80%;
  }
`;

function hexToRgba(hex, opacity = 1) {
  if (hex) {
    hex = hex.replace(
      /^#?([a-f\d])([a-f\d])([a-f\d])$/i,
      (_, r, g, b) => r + r + g + g + b + b,
    );
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? `rgba(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}, ${opacity})`
      : null;
  } else {
    return null;
  }
}

const CLIDENavigation = ({ themeColor, navMenuEnabled }) => {
  const [activeMenu, setActiveMenu] = useState(null);
  const [activeSubMenu, setActiveSubMenu] = useState(null);
  const [clickedMenu, setClickedMenu] = useState(null);
  const navRef = useRef(null);

  // Timeouts for hover delays
  const hoverTimeouts = useRef({});

  const navItems = [
    // ... (same navItems array as before)
    {
      id: "analyser",
      label: "CLIDE Analyser",
      icon: <FontAwesomeIcon icon={faChartPie} className="icon" />,
      items: [
        {
          label: "Website",
          href: "https://clideanalyser.com/",
          icon: <FontAwesomeIcon icon={faHome} className="icon" />,
        },
        {
          label: "HEXA AI",
          href: "https://clideanalyser.com/-1",
          icon: <FontAwesomeIcon icon={faBrain} className="icon" />,
        },
        {
          label: "Predictions",
          href: "https://clideanalyser.com/predictive-analytics",
          icon: <FontAwesomeIcon icon={faChartLine} className="icon" />,
        },
        {
          label: "DDS",
          href: "https://clideanalyser.com/dddm",
          icon: <FontAwesomeIcon icon={faDatabase} className="icon" />,
        },
        {
          label: "Insights",
          href: "https://www.linkedin.com/newsletters/nextgen-safety-analytics-7033263041911623680/",
          icon: <FontAwesomeIcon icon={faNewspaper} className="icon" />,
        },
        {
          label: "LinkedIn",
          href: "https://www.linkedin.com/showcase/clideanalysersafetyapp/",
          icon: <FaLinkedinIn icon={faLink} className="icon" />,
        },
        {
          label: "YouTube",
          href: "https://www.youtube.com/watch?v=8gMoTnF4OOM&list=PLBNDUroDKCvOhtbW-ZPJud7NzMPnb5iuf",
          icon: <FaYoutube className="icon" />,
        },
      ],
    },
    {
      id: "clideg",
      label: "CLIDEG",
      icon: <FontAwesomeIcon icon={faUsers} className="icon" />,
      items: [
        {
          id: "Consulting",
          label: "Consulting",
          icon: <FontAwesomeIcon icon={faUsers} className="icon" />,
          items: [
            {
              label: "Diagnostics",
              href: "https://clideg.clideanalyser.com/diagnostic",
              icon: <FontAwesomeIcon icon={faSearchPlus} className="icon" />,
            },
            {
              label: "Analytics",
              href: "https://clideg.clideanalyser.com/analytics",
              icon: <FontAwesomeIcon icon={faChartLine} className="icon" />,
            },
            {
              label: "Transformation",
              href: "https://clideg.clideanalyser.com/transformational",
              icon: <FontAwesomeIcon icon={faRecycle} className="icon" />,
            },
            {
              label: "PPE Modal",
              href: "https://clideg.clideanalyser.com/ppe-model",
              icon: <FontAwesomeIcon icon={faShieldAlt} className="icon" />,
            },
            {
              label: "C-ZAP",
              href: "https://clideg.clideanalyser.com/c-zap",
              icon: <FontAwesomeIcon icon={faWrench} className="icon" />,
            },
          ],
        },
        {
          id: "Training",
          label: "Training",
          icon: <FontAwesomeIcon icon={faGraduationCap} className="icon" />,
          items: [
            {
              label: "BBS",
              href: "https://clideg.clideanalyser.com/bbs",
              icon: (
                <FontAwesomeIcon icon={faChalkboardTeacher} className="icon" />
              ),
            },

            {
              label: "RBPSM",
              href: "https://clideg.clideanalyser.com/rbpsm",
              icon: <FontAwesomeIcon icon={faSchool} className="icon" />,
            },
            {
              label: "Leadership",
              href: "https://clideg.clideanalyser.com/leadership-training",
              icon: <FontAwesomeIcon icon={faUserTie} className="icon" />,
            },
            {
              label: "I-Investigation",
              href: "https://clideg.clideanalyser.com/incident-investigation",
              icon: <FontAwesomeIcon icon={faSearch} className="icon" />,
            },
            {
              label: "Analytics",
              href: "https://clideg.clideanalyser.com/analytics",
              icon: <FontAwesomeIcon icon={faChartLine} className="icon" />,
            },
            {
              label: "Safety Culture",
              href: "https://clideg.clideanalyser.com/sc-training",
              icon: <FontAwesomeIcon icon={faShieldAlt} className="icon" />,
            },
          ],
        },

        {
          label: "Insights",
          href: "https://clideg.clideanalyser.com/blog",
          icon: <FontAwesomeIcon icon={faLightbulb} className="icon" />,
        },
        {
          label: "Newsletter",
          href: "https://www.linkedin.com/newsletters/7247573041063301120/",
          icon: <FontAwesomeIcon icon={faNewspaper} className="icon" />,
        },
        {
          label: "LinkedIn",
          href: "https://www.linkedin.com/company/clideg",
          icon: <FaLinkedinIn icon={faLink} className="icon" />,
        },
        {
          label: "YouTube",
          href: "https://www.youtube.com/watch?v=GwMniWtqvOk&list=PLBNDUroDKCvNNK1PExq0wwPHs7I6R6ych",
          icon: <FaYoutube icon={faVideo} className="icon" />,
        },
      ],
    },
    {
      id: "eclide",
      label: "ECLIDE",
      icon: <FontAwesomeIcon icon={faGraduationCap} className="icon" />,
      items: [
        {
          label: "LMS",
          href: "https://eclide.clideanalyser.com/hse",
          icon: <FontAwesomeIcon icon={faBookOpen} className="icon" />,
        },

        {
          label: "Courses",
          href: "https://eclide.clideanalyser.com/slides",
          icon: <FontAwesomeIcon icon={faGraduationCap} className="icon" />,
        },
        {
          label: "Custom Development",
          href: "https://eclide.clideanalyser.com/",
          icon: <FontAwesomeIcon icon={faLaptopCode} className="icon" />,
        },
        {
          label: "LinkedIn",
          href: "https://www.linkedin.com/showcase/eclidehse",
          icon: <FaLinkedinIn icon={faLink} className="icon" />,
        },
        {
          label: "YouTube",
          href: "https://www.youtube.com/watch?v=iMN6SHxfPa0&list=PLBNDUroDKCvMJWiCBWSGCsiuURJD79yE1",
          icon: <FaYoutube icon={faVideo} className="icon" />,
        },
      ],
    },
    {
      id: "insight",
      label: "CLIDE Insight",
      icon: <FontAwesomeIcon icon={faLightbulb} className="icon" />,
      href: "https://clideg.clideanalyser.com/insight-app",
    },
  ];

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setActiveMenu(null);
        setActiveSubMenu(null);
        setClickedMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setActiveMenu(null);
        setActiveSubMenu(null);
        setClickedMenu(null);
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  // Handle top-level menu hover
  const handleMenuHover = (menuId) => {
    if (window.innerWidth > 768) {
      // Only on desktop
      if (hoverTimeouts.current[menuId]) {
        clearTimeout(hoverTimeouts.current[menuId]);
      }
      setActiveMenu(menuId);
    }
  };

  // Handle top-level menu leave
  const handleMenuLeave = (menuId) => {
    if (window.innerWidth > 768) {
      hoverTimeouts.current[menuId] = setTimeout(() => {
        setActiveMenu((prev) => (prev === menuId ? null : prev));
      }, 300); // 300ms delay before closing
    }
  };

  // Handle submenu hover
  const handleSubMenuHover = (subMenuId) => {
    if (window.innerWidth > 768) {
      // Clear any pending timeouts
      Object.keys(hoverTimeouts.current).forEach((key) => {
        if (key.startsWith("sub-")) {
          clearTimeout(hoverTimeouts.current[key]);
        }
      });
      setActiveSubMenu(subMenuId);
    }
  };

  // Handle submenu leave
  const handleSubMenuLeave = (subMenuId) => {
    if (window.innerWidth > 768) {
      hoverTimeouts.current[`sub-${subMenuId}`] = setTimeout(() => {
        setActiveSubMenu((prev) => (prev === subMenuId ? null : prev));
      }, 100); // Reduced to 100ms for faster response
    }
  };

  // Handle menu toggle - CLICK ONLY for mobile/touch
  const handleMenuToggle = (menuId) => {
    if (window.innerWidth <= 768) {
      // Mobile click behavior
      if (clickedMenu === menuId) {
        setClickedMenu(null);
        setActiveMenu(null);
      } else {
        setClickedMenu(menuId);
        setActiveMenu(menuId);
      }
      setActiveSubMenu(null);
    }
  };

  // Handle submenu toggle - CLICK ONLY for mobile/touch
  const handleSubMenuToggle = (subMenuId, e) => {
    if (window.innerWidth <= 768) {
      // Mobile click behavior
      e.stopPropagation();
      setActiveSubMenu(activeSubMenu === subMenuId ? null : subMenuId);
    }
  };

  // Handle link click
  const handleLinkClick = () => {
    setActiveMenu(null);
    setActiveSubMenu(null);
    setClickedMenu(null);
  };

  const renderDropdownItems = (items, parentId = "", themeColor) => {
    return (
      <DropdownMenu $isOpen={true}>
        <DropdownList>
          {items.map((item, index) => {
            const itemId = `${parentId}-${index}`;
            const hasSubmenu = item.items && item.items.length > 0;
            const isSubMenuOpen = activeSubMenu === itemId;

            return (
              <DropdownItem
                key={itemId}
                onMouseEnter={() => hasSubmenu && handleSubMenuHover(itemId)}
                onMouseLeave={() => hasSubmenu && handleSubMenuLeave(itemId)}
              >
                {hasSubmenu ? (
                  <>
                    <SubMenuTrigger
                      onClick={(e) => handleSubMenuToggle(itemId, e)}
                      themeColor={themeColor}
                    >
                      <MenuIcon className="menu-icon">
                        {item.icon}
                        {item.label}
                      </MenuIcon>
                      <SubMenuArrow className="submenu-arrow">
                        <FontAwesomeIcon
                          icon={faChevronRight}
                          className="icon"
                        />
                      </SubMenuArrow>
                    </SubMenuTrigger>

                    <SubMenu
                      $isOpen={isSubMenuOpen}
                      onMouseEnter={() => handleSubMenuHover(itemId)}
                      onMouseLeave={() => handleSubMenuLeave(itemId)}
                    >
                      <DropdownList>
                        {item.items.map((subItem, subIndex) => (
                          <DropdownItem key={subIndex}>
                            <DropdownLink
                              href={subItem.href}
                              onClick={handleLinkClick}
                              target="_blank"
                              rel="noopener noreferrer"
                              $themeColor={themeColor}
                            >
                              {subItem.icon}
                              {subItem.label}
                            </DropdownLink>
                          </DropdownItem>
                        ))}
                      </DropdownList>
                    </SubMenu>
                  </>
                ) : (
                  <DropdownLink
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleLinkClick}
                    $themeColor={themeColor}
                  >
                    {item.icon}
                    {item.label}
                  </DropdownLink>
                )}
              </DropdownItem>
            );
          })}
        </DropdownList>
      </DropdownMenu>
    );
  };

  return (
    <NavContainer ref={navRef} $themeColor={themeColor}>
      <NavInner>
        <Logo className="mr-3" $themeColor={themeColor}>
          <img
            className="logo-img"
            src={require("assets/images/CLIDEOffice.png")}
            alt="Logo"
          />
        </Logo>

        <NavItems>
          {!!navMenuEnabled &&
            navItems.map((item) => {
              if (item.href) {
                return (
                  <NavItem key={item.id}>
                    <NavLink
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={handleLinkClick}
                      $themeColor={themeColor}
                    >
                      {item.icon}
                      {item.label}
                    </NavLink>
                    <ActiveIndicator />
                  </NavItem>
                );
              }

              return (
                <NavItem
                  key={item.id}
                  onMouseEnter={() => handleMenuHover(item.id)}
                  onMouseLeave={() => handleMenuLeave(item.id)}
                >
                  <NavButton
                    onClick={() => handleMenuToggle(item.id)}
                    onMouseEnter={() => handleMenuHover(item.id)}
                    className={activeMenu === item.id ? "active" : ""}
                    $themeColor={themeColor}
                  >
                    {item.icon}
                    {item.label}
                    <DropdownArrow $isOpen={activeMenu === item.id}>
                      <FontAwesomeIcon icon={faChevronDown} className="icon" />
                    </DropdownArrow>
                  </NavButton>
                  <ActiveIndicator />

                  {activeMenu === item.id &&
                    item.items &&
                    renderDropdownItems(item.items, item.id, themeColor)}
                </NavItem>
              );
            })}
        </NavItems>

        <Tagline $themeColor={themeColor}>
          <p>Get actionable insight from your safety data</p>
        </Tagline>
      </NavInner>
    </NavContainer>
  );
};

export default CLIDENavigation;
